/**
 * 업적 달성 체크 시스템
 * 매 틱마다 조건 확인 (최적화 필요)
 */

import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import { ACHIEVEMENTS, type AchievementConfig } from '../config/achievements.js';
import { canUnlockPrestige2 } from '../config/prestige.js';

function checkAchievementRequirement(state: GameState, achievement: AchievementConfig): boolean {
  const { requirement } = achievement;

  switch (requirement.type) {
    case 'totalEarned':
      return state.statistics.totalEarned >= requirement.value;

    case 'generatorCount':
      if (!requirement.generatorId) return false;
      const gen = state.generators[requirement.generatorId];
      return gen ? gen.count >= requirement.value : false;

    case 'prestigeCount':
      return state.prestige.count >= requirement.value;

    case 'prestige2Count':
      return state.prestige2.count >= requirement.value;

    case 'blockchainCount':
      return state.prestige.blockchain >= requirement.value;

    case 'totalClicks':
      return state.statistics.totalClicks >= requirement.value;

    case 'maxCombo':
      return state.combo.maxCombo >= requirement.value;

    case 'goldenClicks':
      return state.statistics.totalGoldenClicks >= requirement.value;

    case 'luckyGoldenCount':
      return state.statistics.luckyGoldenCount >= requirement.value;

    case 'adsWatched':
      return state.statistics.adsWatched >= requirement.value;

    case 'offlineClaims':
      return state.statistics.offlineClaims >= requirement.value;

    case 'dailyStreak':
      return state.dailyBonus.streak >= requirement.value;

    case 'dogeClicks':
      return state.statistics.dogeClicks >= requirement.value;

    case 'allGenerators100':
      return Object.values(state.generators).every(gen => gen.count >= 100);

    case 'fastEarning':
      // 환생 없이 1시간 내 1B 생산 - 플레이 시간 체크 필요
      // 간단히 totalEarnedThisRun과 playTime으로 대체
      const oneHour = 3600;
      return state.totalEarnedThisRun >= requirement.value && state.statistics.playTime <= oneHour;

    case 'allAchievements':
      // 자기 자신 제외하고 모든 업적 달성
      const otherAchievements = ACHIEVEMENTS.filter(a => a.id !== achievement.id);
      return otherAchievements.every(a => state.achievements[a.id]?.unlocked);

    default:
      return false;
  }
}

export const achievementSystem: TickSystem<GameState, GameAction> = {
  name: 'achievement',

  update(state) {
    if (!state.isRunning) return state;

    let changed = false;
    let newState = { ...state };

    // 모든 업적 체크 (잠금 해제되지 않은 것만)
    for (const achConfig of ACHIEVEMENTS) {
      const ach = state.achievements[achConfig.id];
      if (!ach || ach.unlocked) continue;

      if (checkAchievementRequirement(state, achConfig)) {
        newState.achievements = {
          ...newState.achievements,
          [achConfig.id]: {
            ...ach,
            unlocked: true,
            claimedAt: Date.now()
          }
        };
        changed = true;
      }
    }

    // 2차 환생 해금 체크
    if (!state.prestige2.unlocked) {
      if (canUnlockPrestige2(state.prestige.count, state.prestige.blockchain)) {
        newState.prestige2 = {
          ...state.prestige2,
          unlocked: true
        };
        changed = true;
      }
    }

    // 일일 보너스 갱신 체크
    const today = new Date().toISOString().split('T')[0];
    if (state.dailyBonus.lastClaimDate !== today && !state.dailyBonus.canClaim) {
      newState.dailyBonus = {
        ...state.dailyBonus,
        canClaim: true
      };
      changed = true;
    }

    return changed ? newState : state;
  }
};
